import {computed, type ComputedRef, type Ref} from "vue";
import {
  createGrid,
  type ColDef,
  type ColGroupDef,
  type GridApi,
  type GridOptions,
  type IServerSideDatasource,
  type SetFilterValuesFuncParams,
  type SortModelItem,
} from "ag-grid-community";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import type {
  EnhancedTableCompactRow,
  EnhancedTableData,
  EnhancedTableRowData,
} from "@/components/bi/biChartTypes.ts";

export const ENHANCED_TABLE_DEFAULT_SERVER_PAGE_SIZE = 100;
export const ENHANCED_TABLE_SERVER_PAGE_SIZES = [25, 50, 100, 250, 500, 1000, 2500, 5000, 50000];

interface UseEnhancedTableServerRowsArgs {
  chartLine: Ref<any>;
  enhancedTableData: ComputedRef<EnhancedTableData | undefined>;
  toRowData: (rows: EnhancedTableCompactRow[]) => EnhancedTableRowData[];
  /** Called when the server no longer holds the rows (session expired or evicted); the widget should re-fetch. */
  onExpired: () => void;
}

/**
 * Server-side row model for the EnhancedTable widget (tableOptions.rowModel = 'serverSide'). The server keeps
 * the built rows in the user's session under a token (see BIEnhancedTableServerRows.java); this composable
 * pages them into AG Grid, feeds the set filters, and exports every filtered row to Excel.
 */
export function useEnhancedTableServerRows({chartLine, enhancedTableData, toRowData, onExpired}: UseEnhancedTableServerRowsArgs) {
  const serverSide = computed(() => enhancedTableData.value?.serverSide);
  const isServerSide = computed(() => !!serverSide.value);
  const serverPageSize = computed(
    () => enhancedTableData.value?.tableOptions?.serverPageSize || ENHANCED_TABLE_DEFAULT_SERVER_PAGE_SIZE);
  const cacheBlockSize = computed(() => serverPageSize.value < 100 ? serverPageSize.value * 2 : serverPageSize.value);

  async function fetchRows(token: string, startRow: number, endRow: number, filterModel: unknown,
                           sortModel: SortModelItem[], noLoadingIndicator: boolean) {
    const response: any = await NamaCommon.namaFetch(NamaURLS.FetchEnhancedTableRows, {
      data: {token, startRow, endRow, filterModel, sortModel},
      noLoadingIndicator,
    });
    if (!response || response.expired) {
      onExpired();
      return undefined;
    }
    const rows = (response.rows ?? []) as EnhancedTableCompactRow[];
    rows.forEach(applyRowInteractionPoints);
    return {rowData: toRowData(rows), rowCount: response.rowCount as number};
  }

  // Interaction point arrays stay empty on the client until their rows arrive; each fetched row carries its
  // own points, written back at the row's source index so click / drill / link lookups by _biRowIdx work.
  function applyRowInteractionPoints(row: EnhancedTableCompactRow) {
    const points = row.ip;
    const sourceIndex = row.i;
    const interactionData = chartLine.value?.interactionData;
    delete row.ip;
    if (!points || sourceIndex == null || !interactionData) return;
    points.ce?.forEach((point, mappingIndex) => {
      const mappingPoints = interactionData.clickEmitData?.[mappingIndex]?.points;
      if (mappingPoints) mappingPoints[sourceIndex] = point;
    });
    if ('dd' in points && interactionData.drillDownData?.points) interactionData.drillDownData.points[sourceIndex] = points.dd;
    if ('lk' in points && interactionData.linkData?.points) interactionData.linkData.points[sourceIndex] = points.lk;
    if ('db' in points && interactionData.drillDownByData?.points) interactionData.drillDownByData.points[sourceIndex] = points.db;
  }

  const serverSideDatasource = computed<IServerSideDatasource | undefined>(() => {
    const token = serverSide.value?.token;
    if (!token) return undefined;
    return {
      getRows: async params => {
        const {startRow, endRow, filterModel, sortModel} = params.request;
        const result = await fetchRows(token, startRow ?? 0, endRow ?? serverSide.value?.rowCount ?? 0, filterModel, sortModel, true);
        if (result) params.success(result);
        else params.fail();
      },
    };
  });

  async function fetchSetFilterValues(params: SetFilterValuesFuncParams) {
    const token = serverSide.value?.token;
    const colId = params.colDef.colId ?? params.colDef.field;
    if (!token || !colId) {
      params.success([]);
      return;
    }
    const response: any = await NamaCommon.namaFetch(NamaURLS.FetchEnhancedTableColumnValues, {
      data: {token, colId},
      noLoadingIndicator: true,
    });
    if (!response || response.expired) {
      params.success([]);
      onExpired();
      return;
    }
    params.success(response.values ?? []);
  }

  /**
   * AG Grid's server-side model can only export the blocks it has loaded, so the full filtered and sorted
   * result is fetched and exported through a throwaway client-side grid built from the same column
   * definitions (so formatting and the visible-text export of HTML cells match the client-side mode).
   */
  async function exportAllRowsToExcel(sourceApi: GridApi, exportGridOptions: GridOptions, fileName: string) {
    const token = serverSide.value?.token;
    if (!token) return;
    const result = await fetchRows(token, 0, serverSide.value?.rowCount ?? 0, sourceApi.getFilterModel(),
      currentSortModel(sourceApi), false);
    if (!result) return;
    const host = document.createElement('div');
    host.style.cssText = 'position:absolute;left:-10000px;top:0;width:800px;height:200px;';
    document.body.appendChild(host);
    const exportApi = createGrid(host, {
      ...exportGridOptions,
      columnDefs: withoutCellRenderers(exportGridOptions.columnDefs ?? []),
      rowModelType: 'clientSide',
      rowData: result.rowData,
    });
    try {
      exportApi.applyColumnState({state: sourceApi.getColumnState(), applyOrder: true});
      // exportDataAsExcel finishes asynchronously and would read the rows after the grid is destroyed below,
      // writing a header-only file; getDataAsExcel builds the workbook synchronously.
      const workbook = exportApi.getDataAsExcel({fileName});
      if (workbook instanceof Blob)
        saveFile(workbook, fileName);
    } finally {
      exportApi.destroy();
      host.remove();
    }
  }

  return {
    isServerSide,
    serverPageSize,
    cacheBlockSize,
    serverSideDatasource,
    fetchSetFilterValues,
    exportAllRowsToExcel,
  };
}

function saveFile(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function currentSortModel(api: GridApi): SortModelItem[] {
  return api.getColumnState()
    .filter(c => c.sort)
    .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
    .map(c => ({colId: c.colId, sort: c.sort!}));
}

// The throwaway export grid lives outside Vue, so the Vue cell renderers cannot mount there; export reads
// values and formatters only.
function withoutCellRenderers(defs: (ColDef | ColGroupDef)[]): (ColDef | ColGroupDef)[] {
  return defs.map(def => 'children' in def
    ? {...def, children: withoutCellRenderers(def.children)}
    : {...def, cellRenderer: undefined, cellRendererParams: undefined});
}
